import { EventBadge } from '@/components/shared/EventBadge';
import { LoadingState, OverlayLoader } from '@/components/shared/OverlayLoader';
import { PostDialog } from '@/components/shared/PostDialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCampaignContext } from '@/contexts/CampaignContext';
import { Post } from '@/hooks/useCampaigns';
import '@/styles/calendar.css';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  Calendar,
  dateFnsLocalizer,
  NavigateAction,
  View,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'en-GB': require('date-fns/locale/en-GB'),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

type ContentCalendarProps = {
  overlayLoader?: LoadingState;
};

export const ContentCalendar = ({ overlayLoader }: ContentCalendarProps) => {
  const { campaigns, activeCampaign } = useCampaignContext();

  const events = useMemo(() => {
    if (!campaigns) return [];

    let relevantCampaigns = campaigns;
    if (activeCampaign) {
      relevantCampaigns = [activeCampaign];
    }

    return relevantCampaigns.flatMap((campaign) =>
      (campaign.posts || []).map((post) => {
        const startDate = new Date(post.dateTime);
        const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

        return {
          ...post,
          start: startDate,
          end: endDate,
          campaign: campaign.name,
          allDay: false,
        };
      }),
    );
  }, [campaigns, activeCampaign]);

  return (
    <Card className="relative">
      <OverlayLoader {...overlayLoader} />
      <div className="p-5 max-w-[calc(100vw-16rem)] mx-auto h-[calc(100vh-16rem)]">
        <Calendar<
          Post & { start: Date; end: Date; campaign: string; allDay: boolean }
        >
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={['month', 'week']}
          defaultView="month"
          tooltipAccessor={(event) => `${event.campaign} - ${event.format}`}
          components={{
            toolbar: (toolbarProps) => <CustomToolbar {...toolbarProps} />,
            event: EventCard,
          }}
        />
      </div>
    </Card>
  );
};

type CustomToolbarProps = {
  date: Date;
  view: View;
  onNavigate: (action: NavigateAction) => void;
  onView: (view: View) => void;
};

const CustomToolbar = ({
  date,
  onNavigate,
  onView,
  view,
}: CustomToolbarProps) => {
  const dateFormat = useMemo(() => {
    switch (view) {
      case 'week':
      case 'month':
      default:
        return (
          <h3 className="text-lg font-semibold">{format(date, 'MMMM yyyy')}</h3>
        );
    }
  }, [date, view]);

  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onNavigate('TODAY')}>
          Today
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate('PREV')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate('NEXT')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="min-w-[140px]">{dateFormat}</div>
      <div className="flex items-center gap-4">
        <Select value={view} onValueChange={(value) => onView(value as View)}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month" className="cursor-pointer">
              Month
            </SelectItem>
            <SelectItem value="week" className="cursor-pointer">
              Week
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

interface EventProps {
  event: Post & {
    start: Date;
    end: Date;
    campaign: string;
    allDay: boolean;
  };
}

const EventCard = ({ event }: EventProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}>
        <Card
          onClick={() => setIsDialogOpen(true)}
          className="p-3 mx-3 bg-card shadow-sm hover:shadow-md transition-shadow space-y-2 cursor-pointer">
          <div className="flex items-center gap-2">
            <EventBadge format={event.format} />
          </div>

          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">
              Caption Idea
            </div>
            <div className="text-sm line-clamp-2 text-wrap">
              {event.caption}
            </div>
          </div>
        </Card>
      </motion.div>

      <PostDialog
        post={event}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};
